import {Button} from '@wandb/weave/components/Button';
import {TextField} from '@wandb/weave/components/Form/TextField';
import {Icon} from '@wandb/weave/components/Icon';
import {IconName} from '@wandb/weave/components/Icon/types';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {createPortal} from 'react-dom';

import {useEntityProject} from '../../context';
import {useGetTraceServerClientContext} from '../wfReactInterface/traceServerClientContext';
import {
  projectIdFromParts,
  useRootObjectVersions,
} from '../wfReactInterface/tsDataModelHooks';
import {
  KnownBaseObjectClassType,
  ObjectVersionSchema,
} from '../wfReactInterface/wfDataModelHooksInterface';

// Component spacing constants
const DROPDOWN_GAP_PX = 4;
// Component width constants
const MIN_WIDTH = 226;
const MAX_WIDTH = 448;
const ICON_PADDING = 34;
const BASE_PADDING = 16;
const ACTIONS_PADDING = 56;

// Focus navigation constants
const ALL_ITEMS_INDEX = 0; // "All X" option is at index 0
const NO_FOCUS = null; // null indicates no item is focused

// Debounce delay for search input
const DEBOUNCE_DELAY_MS = 300;

// Debounce delay for selection changes (before triggering data fetch)
const SELECTION_DEBOUNCE_MS = 750;

/**
 * Custom hook to debounce a value
 */
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

const formatVersionTimestamp = (timestampMs: number): string => {
  return new Date(timestampMs).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const parseValue = (value: string | null): [string | null, string | null] => {
  if (!value) {
    return [null, null];
  }
  const parts = value.split(':');
  return parts.length === 2 ? [parts[0], parts[1]] : [value, null];
};

interface ObjectVersionSelectorProps {
  objectClass: KnownBaseObjectClassType;
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  icon?: IconName;
  disabled?: boolean;
}

// Reducer state and actions
type SelectorState = {
  // Dropdown state
  isOpen: boolean;
  dropdownPosition: {top: number; left: number} | null;

  // Navigation & focus (null = no focus, 0 = "All X", 1+ = items)
  activePane: 'objects' | 'versions';
  focusedIndex: number | null;
  versionFocusedIndex: number | null;

  // Search & text field
  textFieldValue: string;
  searchQuery: string;

  // Display state
  displayedName: string | null; // which object's versions to show
  measuredWidth: number;
};

type SelectorAction =
  | {type: 'OPEN_DROPDOWN'; position: {top: number; left: number}}
  | {type: 'CLOSE_DROPDOWN'}
  | {type: 'SET_FOCUS'; index: number | null}
  | {type: 'SET_VERSION_FOCUS'; index: number | null}
  | {type: 'SET_ACTIVE_PANE'; pane: 'objects' | 'versions'}
  | {type: 'SET_TEXT_FIELD_VALUE'; value: string}
  | {type: 'SET_SEARCH_QUERY'; query: string}
  | {type: 'SET_DISPLAYED_NAME'; name: string | null}
  | {type: 'SET_MEASURED_WIDTH'; width: number}
  | {type: 'RESET_FOR_OPEN'};

function selectorReducer(
  state: SelectorState,
  action: SelectorAction
): SelectorState {
  switch (action.type) {
    case 'OPEN_DROPDOWN':
      return {
        ...state,
        isOpen: true,
        dropdownPosition: action.position,
      };
    case 'CLOSE_DROPDOWN':
      return {
        ...state,
        isOpen: false,
      };
    case 'SET_FOCUS':
      return {
        ...state,
        focusedIndex: action.index,
      };
    case 'SET_VERSION_FOCUS':
      return {
        ...state,
        versionFocusedIndex: action.index,
      };
    case 'SET_ACTIVE_PANE':
      return {
        ...state,
        activePane: action.pane,
      };
    case 'SET_TEXT_FIELD_VALUE':
      return {
        ...state,
        textFieldValue: action.value,
      };
    case 'SET_SEARCH_QUERY':
      return {
        ...state,
        searchQuery: action.query,
      };
    case 'SET_DISPLAYED_NAME':
      return {
        ...state,
        displayedName: action.name,
      };
    case 'SET_MEASURED_WIDTH':
      return {
        ...state,
        measuredWidth: action.width,
      };
    case 'RESET_FOR_OPEN':
      return {
        ...state,
        searchQuery: '',
        focusedIndex: NO_FOCUS,
        versionFocusedIndex: NO_FOCUS,
        activePane: 'objects',
      };
    default:
      return state;
  }
}

/**
 * A reusable component for selecting object names and versions.
 *
 * Displays a single dropdown with a two-pane interface:
 * - Left pane: scrollable list of object names
 * - Right pane (shown after selection): versions for the selected name
 *
 * Clicking a name shows its versions and selects "name + all versions".
 * Clicking a version selects "name + specific version".
 * The popover remains open after selection, allowing multiple changes.
 * Clicking outside or pressing Escape dismisses the popover.
 *
 * Entity and project are obtained from EntityProjectContext.
 *
 * @param objectClass - The object class to fetch (e.g., 'Dataset', 'Evaluation')
 * @param value - Current selection in format "name:hash" or "name"
 * @param onChange - Callback when selection changes
 * @param placeholder - Placeholder text for the selector
 * @param icon - Optional icon to display
 * @param disabled - Whether the selector is disabled
 */
export const ObjectVersionSelector: React.FC<ObjectVersionSelectorProps> = ({
  objectClass,
  value,
  onChange,
  placeholder = 'Select object',
  icon,
  disabled = false,
}) => {
  const {entity, project} = useEntityProject();

  // Use useReducer for all UI state
  const [state, dispatch] = React.useReducer(selectorReducer, {
    isOpen: false,
    dropdownPosition: null,
    activePane: 'objects',
    focusedIndex: NO_FOCUS,
    versionFocusedIndex: NO_FOCUS,
    textFieldValue: '',
    searchQuery: '',
    displayedName: null,
    measuredWidth: MIN_WIDTH,
  });

  // Version cache with loading/error states to prevent race conditions
  const [versionCache, setVersionCache] = useState<
    Map<
      string,
      {
        status: 'loading' | 'loaded' | 'error';
        versions?: ObjectVersionSchema[];
        error?: string;
      }
    >
  >(new Map());

  // Ref to track version cache without causing callback recreation
  const versionCacheRef = useRef(versionCache);
  versionCacheRef.current = versionCache;

  // State for debounced selection changes
  const [pendingSelection, setPendingSelection] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Debounce search query for filtering
  const debouncedSearchQuery = useDebounce(
    state.searchQuery,
    DEBOUNCE_DELAY_MS
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const focusedItemRef = useRef<HTMLDivElement>(null);
  const measuringRef = useRef<HTMLSpanElement>(null);

  // Fetch only latest versions to get object names
  const objectsQuery = useRootObjectVersions({
    entity,
    project,
    filter: {baseObjectClasses: [objectClass], latestOnly: true},
    metadataOnly: true,
  });

  const names = useMemo(() => {
    const items = objectsQuery.result ?? [];
    return items.map(item => item.objectId).sort((a, b) => a.localeCompare(b));
  }, [objectsQuery.result]);

  // Filter names based on debounced search query
  const filteredNames = useMemo(() => {
    if (!debouncedSearchQuery.trim()) {
      return names;
    }
    const lowerQuery = debouncedSearchQuery.toLowerCase();
    return names.filter(name => name.toLowerCase().includes(lowerQuery));
  }, [names, debouncedSearchQuery]);

  const getTsClient = useGetTraceServerClientContext();

  const [selectedName, selectedHash] = useMemo(
    () => parseValue(value),
    [value]
  );

  // Parse pending selection for visual active state
  const [pendingName, pendingHash] = useMemo(
    () => parseValue(pendingSelection),
    [pendingSelection]
  );

  const hoveredVersions = useMemo(() => {
    if (!state.displayedName) {
      return [];
    }
    return versionCache.get(state.displayedName)?.versions || [];
  }, [state.displayedName, versionCache]);

  const versionLoadingState = useMemo(() => {
    if (!state.displayedName) {
      return null;
    }
    return versionCache.get(state.displayedName)?.status || null;
  }, [state.displayedName, versionCache]);

  const versionError = useMemo(() => {
    if (!state.displayedName) {
      return null;
    }
    return versionCache.get(state.displayedName)?.error || null;
  }, [state.displayedName, versionCache]);

  // Fetch versions for a specific object
  const fetchVersionsForObject = useCallback(
    async (objectId: string) => {
      const cachedEntry = versionCacheRef.current.get(objectId);

      // Skip if already loaded or currently loading (prevents race condition)
      if (
        cachedEntry?.status === 'loaded' ||
        cachedEntry?.status === 'loading'
      ) {
        return;
      }

      // Set loading state
      setVersionCache(prev => new Map(prev).set(objectId, {status: 'loading'}));

      try {
        const client = getTsClient();
        const response = await client.objsQuery({
          project_id: projectIdFromParts({entity, project}),
          filter: {
            object_ids: [objectId],
            is_op: false,
          },
          metadata_only: true,
        });

        const versions = (response.objs || []).map(obj => ({
          entity,
          project,
          objectId: obj.object_id,
          versionHash: obj.digest,
          versionIndex: obj.version_index,
          path: '',
          scheme: 'weave' as const,
          weaveKind: obj.kind || 'unknown',
          baseObjectClass: obj.base_object_class || null,
          createdAtMs: obj.created_at ? new Date(obj.created_at).getTime() : 0,
          val: obj.val,
          userId: obj.wb_user_id || undefined,
        }));

        versions.sort((a, b) => b.versionIndex - a.versionIndex);

        setVersionCache(prev =>
          new Map(prev).set(objectId, {
            status: 'loaded',
            versions,
          })
        );
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        console.error('Failed to fetch versions:', error);
        setVersionCache(prev =>
          new Map(prev).set(objectId, {
            status: 'error',
            error: errorMessage,
          })
        );
      }
    },
    [getTsClient, entity, project]
  );

  useEffect(() => {
    if (selectedName) {
      fetchVersionsForObject(selectedName);
    }
  }, [selectedName, fetchVersionsForObject]);

  // Debounced onChange effect - waits for both object and version selection to complete
  useEffect(() => {
    // Clear existing timer if selection changes
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Only start debounce if there's a pending selection
    if (pendingSelection !== null) {
      // Check if versions are loaded for the selected object
      const [name] = parseValue(pendingSelection);
      const isVersionsLoaded = name
        ? versionCache.get(name)?.status === 'loaded'
        : true; // If no name, it's "All X" which doesn't need versions

      if (isVersionsLoaded) {
        // Start 3-second timer before calling onChange
        debounceTimerRef.current = setTimeout(() => {
          onChange(pendingSelection);
          setPendingSelection(null);
        }, SELECTION_DEBOUNCE_MS);
      }
    }

    // Cleanup timer on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [pendingSelection, versionCache, onChange]);

  const handleNameClick = useCallback(
    (name: string) => {
      dispatch({type: 'SET_DISPLAYED_NAME', name}); // Show versions for this name
      fetchVersionsForObject(name); // Fetch versions for the selected name
      setPendingSelection(name); // Set pending selection (will trigger debounced onChange)
      dispatch({type: 'SET_TEXT_FIELD_VALUE', value: name}); // Update TextField to show selection
      dispatch({type: 'SET_SEARCH_QUERY', query: ''}); // Clear search to show all objects
    },
    [fetchVersionsForObject]
  );

  const handleVersionClick = useCallback(
    (name: string, hash: string) => {
      setPendingSelection(`${name}:${hash}`); // Set pending selection (will trigger debounced onChange)
      // Update TextField to show the selected version
      const version = versionCache
        .get(name)
        ?.versions?.find(v => v.versionHash === hash);
      const versionIndex = version?.versionIndex ?? '?';
      dispatch({
        type: 'SET_TEXT_FIELD_VALUE',
        value: `${name} (v${versionIndex})`,
      });
      dispatch({type: 'SET_SEARCH_QUERY', query: ''}); // Clear search to show all objects
    },
    [versionCache]
  );

  const handleAllVersionsClick = useCallback(
    (name: string) => {
      handleNameClick(name);
    },
    [handleNameClick]
  );

  // Helper to immediately flush pending selection (used when closing dropdown)
  const flushPendingSelection = useCallback(() => {
    if (pendingSelection !== null) {
      // Cancel debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      // Immediately call onChange
      onChange(pendingSelection);
      setPendingSelection(null);
    }
  }, [pendingSelection, onChange]);

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      // Cancel any pending debounced onChange
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      setPendingSelection(null);
      dispatch({type: 'SET_DISPLAYED_NAME', name: null}); // Hide the version selector on the right
      onChange(null); // Clear immediately (no debounce)
      dispatch({type: 'CLOSE_DROPDOWN'}); // Close the popover
    },
    [onChange]
  );

  // Unified keyboard navigation handler for both panes
  const handlePaneKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const isObjectsPane = state.activePane === 'objects';
      const isVersionsPane = state.activePane === 'versions';

      // Guard: versions pane requires displayedName
      if (isVersionsPane && !state.displayedName) {
        return;
      }

      // Get pane-specific configuration
      const focusedIndex = isObjectsPane
        ? state.focusedIndex
        : state.versionFocusedIndex;
      const maxIndex = isObjectsPane
        ? filteredNames.length
        : hoveredVersions.length;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const currentIndex = focusedIndex ?? -1;
        const nextIndex = Math.min(currentIndex + 1, maxIndex);
        dispatch({
          type: isObjectsPane ? 'SET_FOCUS' : 'SET_VERSION_FOCUS',
          index: nextIndex,
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const currentIndex = focusedIndex ?? 1;
        const nextIndex = Math.max(currentIndex - 1, ALL_ITEMS_INDEX);
        dispatch({
          type: isObjectsPane ? 'SET_FOCUS' : 'SET_VERSION_FOCUS',
          index: nextIndex,
        });
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (focusedIndex === ALL_ITEMS_INDEX) {
          // Handle "All X" selection
          if (isObjectsPane) {
            // Cancel any pending debounced onChange
            if (debounceTimerRef.current) {
              clearTimeout(debounceTimerRef.current);
              debounceTimerRef.current = null;
            }
            setPendingSelection(null);
            // Select "All [objectClass]s" and close dropdown
            onChange(null); // Immediate (no debounce)
            dispatch({type: 'SET_DISPLAYED_NAME', name: null});
            dispatch({type: 'CLOSE_DROPDOWN'});
          } else {
            // Select "All versions" but keep dropdown open
            if (state.displayedName) {
              handleNameClick(state.displayedName);
            }
          }
        } else if (focusedIndex !== null && focusedIndex > 0) {
          // Handle specific item selection
          if (isObjectsPane) {
            const name = filteredNames[focusedIndex - 1];
            if (name) {
              handleNameClick(name);
            }
          } else {
            const version = hoveredVersions[focusedIndex - 1];
            if (version && state.displayedName) {
              handleVersionClick(state.displayedName, version.versionHash);
            }
          }
        }
      } else if (
        e.key === 'ArrowRight' &&
        isObjectsPane &&
        state.displayedName
      ) {
        // Objects pane: switch to versions pane
        e.preventDefault();
        dispatch({type: 'SET_ACTIVE_PANE', pane: 'versions'});
        dispatch({type: 'SET_VERSION_FOCUS', index: ALL_ITEMS_INDEX});
      } else if (e.key === 'ArrowLeft' && isVersionsPane) {
        // Versions pane: switch to objects pane
        e.preventDefault();
        dispatch({type: 'SET_ACTIVE_PANE', pane: 'objects'});
      } else if (e.key === 'Escape') {
        e.preventDefault();
        flushPendingSelection(); // Trigger data fetch immediately if there's a pending selection
        dispatch({type: 'CLOSE_DROPDOWN'});
      }
    },
    [
      state.activePane,
      state.focusedIndex,
      state.versionFocusedIndex,
      state.displayedName,
      filteredNames,
      hoveredVersions,
      handleNameClick,
      handleVersionClick,
      onChange,
      flushPendingSelection,
    ]
  );

  useEffect(() => {
    if (!state.isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        flushPendingSelection(); // Trigger data fetch immediately if there's a pending selection
        dispatch({type: 'CLOSE_DROPDOWN'});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [state.isOpen, flushPendingSelection]);

  // Scroll focused item into view (for both objects and versions panes)
  useEffect(() => {
    if (focusedItemRef.current) {
      focusedItemRef.current.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [state.focusedIndex, state.versionFocusedIndex, state.activePane]);

  // Compute display value for TextField
  const displayValue = useMemo(() => {
    // When user is typing (panel is open), show their input
    if (state.isOpen) {
      return state.textFieldValue;
    }
    // When not typing, show the selected value
    if (!selectedName) {
      return `All ${objectClass.toLowerCase()}s`;
    }
    if (selectedHash) {
      const versionIndex = versionCache
        .get(selectedName)
        ?.versions?.find(v => v.versionHash === selectedHash)?.versionIndex;
      return `${selectedName} (v${versionIndex ?? '?'})`;
    }
    return selectedName;
  }, [
    state.isOpen,
    state.textFieldValue,
    selectedName,
    selectedHash,
    objectClass,
    versionCache,
  ]);

  // Measure actual text width and calculate container width
  useLayoutEffect(() => {
    if (measuringRef.current) {
      const textWidth = measuringRef.current.getBoundingClientRect().width;
      // Add padding: icon (ICON_PADDING if present) + BASE_PADDING + ACTIONS_PADDING (for close + chevron)
      const iconPadding = icon ? ICON_PADDING : BASE_PADDING;
      const totalWidth = textWidth + iconPadding + ACTIONS_PADDING;
      // Clamp between MIN_WIDTH and MAX_WIDTH
      const clampedWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, totalWidth));
      dispatch({type: 'SET_MEASURED_WIDTH', width: clampedWidth});
    }
  }, [displayValue, icon]);

  const handleTextFieldFocus = useCallback(() => {
    if (!disabled) {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        dispatch({
          type: 'OPEN_DROPDOWN',
          position: {
            top: rect.bottom + DROPDOWN_GAP_PX,
            left: rect.left,
          },
        });
      }
      // Reset state for new dropdown interaction
      dispatch({type: 'RESET_FOR_OPEN'});
      // Clear the field so user can start typing immediately
      dispatch({type: 'SET_TEXT_FIELD_VALUE', value: ''});

      // Initialize focus based on current selection to scroll it into view
      if (selectedName) {
        const nameIndex = filteredNames.indexOf(selectedName);
        if (nameIndex >= 0) {
          dispatch({type: 'SET_FOCUS', index: nameIndex + 1});
        }

        if (selectedHash) {
          dispatch({type: 'SET_DISPLAYED_NAME', name: selectedName});
          const versions = versionCacheRef.current.get(selectedName)?.versions;
          const versionIndex = versions?.findIndex(
            v => v.versionHash === selectedHash
          );
          if (versionIndex !== undefined && versionIndex >= 0) {
            dispatch({type: 'SET_VERSION_FOCUS', index: versionIndex + 1});
            dispatch({type: 'SET_ACTIVE_PANE', pane: 'versions'});
          }
        }
      }
    }
  }, [disabled, selectedName, selectedHash, filteredNames]);

  const handleTextFieldChange = useCallback((value: string) => {
    dispatch({type: 'SET_TEXT_FIELD_VALUE', value});
    dispatch({type: 'SET_SEARCH_QUERY', query: value});
    // Reset focus when search changes so user starts from top of filtered list
    dispatch({type: 'SET_FOCUS', index: NO_FOCUS});
  }, []);

  // Intercept keyboard events from TextField and delegate to navigation handler
  const handleTextFieldKeyDown = useCallback(
    (key: string, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!state.isOpen) {
        return; // Only intercept when dropdown is open
      }

      // Navigation keys that should be delegated to keyboard handler
      const navigationKeys = [
        'ArrowUp',
        'ArrowDown',
        'ArrowLeft',
        'ArrowRight',
        'Enter',
        'Escape',
      ];

      if (navigationKeys.includes(e.key)) {
        e.preventDefault(); // Prevent cursor movement in TextField
        handlePaneKeyDown(e);
      }
      // All other keys (letters, numbers, backspace, etc.) pass through for normal typing/filtering
    },
    [state.isOpen, handlePaneKeyDown]
  );

  return (
    <div
      ref={containerRef}
      className="tw-style text-[16px] relative min-w-[226px] max-w-[448px]"
      style={{width: `${state.measuredWidth}px`}}>
      {/* Design system selects are generally set to 16px. */}
      {/* This protects against an MUI parent overwriting the Tailwind font size. */}
      <style>{`
        .tw-style input {
          font-size: 16px !important;
        }
      `}</style>
      {/* Hidden measuring element to calculate actual text width (more precise than len + `ch`). */}
      <span
        ref={measuringRef}
        style={{
          visibility: 'hidden',
          position: 'absolute',
          whiteSpace: 'pre',
          fontSize: '16px',
          fontFamily: 'inherit',
        }}>
        {displayValue}
      </span>
      <TextField
        value={displayValue}
        onChange={handleTextFieldChange}
        onFocus={handleTextFieldFocus}
        onKeyDown={handleTextFieldKeyDown}
        disabled={disabled}
        placeholder={placeholder}
        icon={icon}
        extraActions={
          <>
            {value && !disabled && (
              <button
                onClick={handleClear}
                className="flex items-center justify-center p-0 mr-8 border-none bg-transparent cursor-pointer text-moon-500 hover:text-moon-800">
                <Icon name="close" width={16} height={16} />
              </button>
            )}
            <Icon
              name={state.isOpen ? 'chevron-up' : 'chevron-down'}
              width={16}
              height={16}
              className="mr-8"
            />
          </>
        }
      />

      {state.isOpen &&
        state.dropdownPosition &&
        createPortal(
          <Tailwind>
            <div
              ref={dropdownRef}
              tabIndex={0}
              className="fixed bg-white border border-moon-250 rounded flex w-[540px] h-[320px]"
              style={{
                top: `${state.dropdownPosition.top}px`,
                left: `${state.dropdownPosition.left}px`,
                zIndex: 2000,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                outline: 'none',
              }}
              onKeyDown={handlePaneKeyDown}>
              {/* Left pane: Object list */}
              <div className="flex-[1_1_70%] flex flex-col border-r border-moon-250">
                {/* Scrollable object list */}
                <div
                  className="scrollbar-hover overflow-y-auto py-6 flex-1"
                  style={{direction: 'rtl'}}>
                  <div style={{direction: 'ltr'}}>
                    {/* "All [objectClass]s" option */}
                    <div
                      ref={
                        state.activePane === 'objects' &&
                        state.focusedIndex === ALL_ITEMS_INDEX
                          ? focusedItemRef
                          : undefined
                      }
                      onClick={() => {
                        // Cancel any pending debounced onChange
                        if (debounceTimerRef.current) {
                          clearTimeout(debounceTimerRef.current);
                          debounceTimerRef.current = null;
                        }
                        setPendingSelection(null);
                        onChange(null); // Select "All X" immediately (no debounce)
                        dispatch({type: 'SET_DISPLAYED_NAME', name: null});
                        dispatch({type: 'CLOSE_DROPDOWN'});
                      }}
                      className={`
                        py-6 px-10 mx-6 rounded cursor-pointer
                        ${(pendingName !== null ? !pendingName : !selectedName) ? 'text-teal-600 bg-teal-300/35 font-semibold' : 'text-moon-800 font-normal hover:bg-moon-100'}
                        ${state.activePane === 'objects' && state.focusedIndex === ALL_ITEMS_INDEX ? 'bg-moon-100' : ''}
                      `}>
                      All {objectClass.toLowerCase()}s
                    </div>

                    {filteredNames.length === 0 && state.searchQuery ? (
                      <div className="p-12 text-moon-500 text-center">
                        No matching objects found
                      </div>
                    ) : (
                      filteredNames.map((name, index) => {
                        const isFocused =
                          state.activePane === 'objects' &&
                          index + 1 === state.focusedIndex;
                        const isActive =
                          pendingName !== null
                            ? pendingName === name
                            : selectedName === name;
                        return (
                          <Tooltip
                            key={name}
                            trigger={
                              <div
                                ref={isFocused ? focusedItemRef : undefined}
                                onClick={() => handleNameClick(name)}
                                className={`
                                py-6 px-10 mx-6 rounded cursor-pointer line-clamp-1
                                ${isActive ? 'text-teal-600 bg-teal-300/35 font-semibold' : 'text-moon-800 font-normal'}
                                ${!isActive ? 'hover:bg-moon-100' : ''}
                                ${isFocused ? 'bg-moon-100' : ''}
                              `}>
                                {name}
                              </div>
                            }
                            content={name}
                          />
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              {state.displayedName && (
                <div className="flex flex-col flex-[1_1_30%] max-w-[30%]">
                  <div className="px-10 py-6 border-b border-moon-250">
                    <Tooltip
                      trigger={
                        <div className="font-semibold text-moon-800 line-clamp-1">
                          {state.displayedName}
                        </div>
                      }
                      content={state.displayedName}
                    />
                  </div>
                  <div className="scrollbar-hover overflow-y-auto py-6 flex-1">
                    {versionLoadingState === 'loading' ? (
                      <div className="p-12 text-moon-500 text-center flex items-center justify-center gap-8">
                        <Icon name="loading" width={16} height={16} />
                        Loading versions...
                      </div>
                    ) : versionLoadingState === 'error' ? (
                      <div className="p-12 text-center flex flex-col items-center gap-8">
                        <div className="text-red-500">
                          Failed to load versions
                        </div>
                        {versionError && (
                          <div className="text-moon-500 text-xs">
                            {versionError}
                          </div>
                        )}
                        <Button
                          size="small"
                          variant="secondary"
                          icon="retry"
                          onClick={() =>
                            state.displayedName &&
                            fetchVersionsForObject(state.displayedName)
                          }>
                          Retry
                        </Button>
                      </div>
                    ) : hoveredVersions.length === 0 ? (
                      <div className="p-12 text-moon-500 text-center">
                        No versions found
                      </div>
                    ) : (
                      <>
                        <div
                          key="all-versions"
                          ref={
                            state.activePane === 'versions' &&
                            state.versionFocusedIndex === ALL_ITEMS_INDEX
                              ? focusedItemRef
                              : undefined
                          }
                          onClick={() =>
                            state.displayedName &&
                            handleAllVersionsClick(state.displayedName)
                          }
                          className={`
                      py-6 px-10 mx-6 rounded cursor-pointer
                      ${(pendingName !== null ? pendingName === state.displayedName && !pendingHash : selectedName === state.displayedName && !selectedHash) ? 'text-teal-600 bg-teal-300/35 font-semibold' : 'text-moon-800 font-normal hover:bg-moon-100'}
                      ${state.activePane === 'versions' && state.versionFocusedIndex === ALL_ITEMS_INDEX ? 'bg-moon-100' : ''}
                    `}>
                          All versions
                        </div>
                        {hoveredVersions.map((version, index) => {
                          const isSelected =
                            pendingName !== null
                              ? pendingName === state.displayedName &&
                                pendingHash === version.versionHash
                              : selectedName === state.displayedName &&
                                selectedHash === version.versionHash;
                          const isFocused =
                            state.activePane === 'versions' &&
                            state.versionFocusedIndex === index + 1;
                          return (
                            <div
                              key={version.versionHash}
                              ref={isFocused ? focusedItemRef : undefined}
                              onClick={() =>
                                state.displayedName &&
                                handleVersionClick(
                                  state.displayedName,
                                  version.versionHash
                                )
                              }
                              className={`
                          py-6 px-10 mx-6 rounded cursor-pointer flex flex-col gap-2
                          ${isSelected ? 'text-teal-600 bg-teal-300/35 font-semibold' : 'text-moon-800 font-normal hover:bg-moon-100'}
                          ${isFocused ? 'bg-moon-100' : ''}
                        `}>
                              <div>
                                v{version.versionIndex}
                                {index === 0 ? ' (latest)' : ''}
                              </div>
                              <div className="text-xs text-moon-500 font-normal">
                                {formatVersionTimestamp(version.createdAtMs)}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Tailwind>,
          document.body
        )}
    </div>
  );
};
